import { LoadingState } from "@material-dev/insights-portal-ui-components/lib/types";
import { AnyAction, PayloadAction, ThunkDispatch, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  TextVariablesClient,
  ITextVariablesClient,
  TextVariableDto,
  RelevancyCheckerStatuses,
  PollingStatuses,
  DownloadRelevancyCheckerQuery,
} from "app/ApiClient";
import {
  RelevancyCheckerDownloadToken,
  RelevancyCheckerInitiateTokens,
  RelevancyCheckerVariable,
  initialRelevancyChecker,
} from "./relevancyCheckerState";
import { IState } from "slices";
import saveAs from "file-saver";
const POLLING_INTERVAL_MS = 5000;
const RELEVANCY_CHECKER_DOWNLOAD_ERROR_MSG =
  "This file cannot be downloaded because relevancy checker for this dataset are not yet available.";

const RELEVANCY_CHECKER = "Relevancy Checker";

const textVariablesClient: ITextVariablesClient = new TextVariablesClient(process.env.REACT_APP_API_URL);

const addRelevancyCheckerInitialToken = (
  token: string,
  projectId?: number,
  variableName?: string,
  status?: RelevancyCheckerStatuses,
) => {
  let relevancyCheckerInitiateTokens: RelevancyCheckerInitiateTokens = {};
  relevancyCheckerInitiateTokens.projectId = projectId;
  relevancyCheckerInitiateTokens = setRelevancyCheckerVariable(
    relevancyCheckerInitiateTokens,
    token,
    variableName,
    status,
  );
  return relevancyCheckerInitiateTokens;
};

const setRelevancyCheckerVariable = (
  relevancyCheckerInitiateTokens: RelevancyCheckerInitiateTokens,
  token: string,
  variableName?: string,
  status?: RelevancyCheckerStatuses,
) => {
  const relevancyCheckerVariable: RelevancyCheckerVariable = {
    token: token,
    variableName: variableName,
    status: status,
  };

  const relevancyCheckerTokens = { ...relevancyCheckerInitiateTokens };
  if (relevancyCheckerTokens.variables == null) {
    relevancyCheckerTokens.variables = [];
  }
  if (relevancyCheckerVariable) {
    relevancyCheckerTokens.variables.push(relevancyCheckerVariable);
  }
  return relevancyCheckerTokens;
};

const deleteRelevancyCheckerToken = (
  relevancyCheckerInitiateTokens: RelevancyCheckerInitiateTokens[],
  token?: string,
  variableName?: string,
  projectId?: number,
) => {
  let relevancyCheckerTokenList = [];
  if (token) {
    relevancyCheckerTokenList = relevancyCheckerInitiateTokens.map((item) => ({
      ...item,
      variables: (item.variables ?? []).filter((v) => v.token !== token),
    }));
  } else {
    relevancyCheckerTokenList = relevancyCheckerInitiateTokens.map((item) => {
      if (item.projectId === projectId) {
        return {
          ...item,
          variables: (item.variables ?? []).filter((v) => v.variableName !== variableName),
        };
      }
      return item;
    });
  }

  return relevancyCheckerTokenList.filter((x) => x.variables?.length !== 0);
};

const updateRelevancyCheckerToken = (
  relevancyCheckerInitiateTokens: RelevancyCheckerInitiateTokens[],
  token: string,
  projectId?: number,
  variableName?: string,
  status?: RelevancyCheckerStatuses,
) => {
  let relevancyCheckerInitiateToken = relevancyCheckerInitiateTokens.find((x) => x.projectId == projectId);
  if (relevancyCheckerInitiateToken) {
    if (relevancyCheckerInitiateTokens) {
      const variable = relevancyCheckerInitiateToken.variables?.find((x) => x.variableName == variableName);
      if (variable) {
        variable.token = token;
        variable.status = status;
      } else {
        relevancyCheckerInitiateToken = setRelevancyCheckerVariable(
          relevancyCheckerInitiateToken,
          token,
          variableName,
          status,
        );
      }
    }
  } else {
    relevancyCheckerInitiateTokens.push(addRelevancyCheckerInitialToken(token, projectId, variableName, status));
  }
  return relevancyCheckerInitiateTokens;
};

const setRelevancyCheckerDownloadToken = (
  relevancyCheckerDownloadTokens: RelevancyCheckerDownloadToken[],
  token: string,
  projectId: number,
) => {
  let relevancyCheckerDownloadToken: RelevancyCheckerDownloadToken | undefined = relevancyCheckerDownloadTokens.find(
    (x) => x.projectId == projectId,
  );
  if (relevancyCheckerDownloadToken) {
    relevancyCheckerDownloadToken.token = token;
  } else {
    relevancyCheckerDownloadToken = {
      token: token,
      projectId: projectId,
    };

    relevancyCheckerDownloadTokens.push(relevancyCheckerDownloadToken);
  }
  return relevancyCheckerDownloadTokens;
};

const deleteRelevancyCheckerDownloadToken = (
  relevancyCheckerDownloadTokens: RelevancyCheckerDownloadToken[],
  token?: string,
  projectId?: number,
) => {
  if (token) {
    return relevancyCheckerDownloadTokens.filter((x) => x.token !== token);
  } else {
    return relevancyCheckerDownloadTokens.filter((x) => x.projectId !== projectId);
  }
};

const setRelevancyDownloadTokenAndGetTokenStatus = (
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
  token: string,
  projectId: number,
) => {
  setTimeout(() => {
    dispatch(
      downloadRelevancyCheckerTokenReceived({
        token: token,
        projectId,
      }),
    );

    dispatch(getRelevancyCheckerDownloadStatus({ token }));
  }, POLLING_INTERVAL_MS);
};

const deleteRelevancyDownloadTokenOnError = (
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
  token: string,
  projectId?: number,
  currentProjectId?: number,
) => {
  dispatch(
    downloadRelevancyCheckerTokenRemoved({
      token: token,
      status: LoadingState.failed,
      projectId: projectId,
      currentProjectId: currentProjectId,
    }),
  );
};

export const initiateRelevancyCheckerVariable = createAsyncThunk(
  "textVariables/initiateRelevancyChecker",
  async (
    { projectId, englishOnly, variables, batteryOrVariableFullname }: { projectId: number; englishOnly: boolean; variables: TextVariableDto[], batteryOrVariableFullname: string | undefined },
    { dispatch },
  ) => {
    const result: Array<{ variable: TextVariableDto; isSuccess: boolean; }> = [];
    const taskList: Array<Promise<unknown>> = [];
    for (const variable of variables) {
      const { variableName = "" } = variable;
      let isSuccess = false;
      try {
        const apiResult = await textVariablesClient.initiateRelevancyChecker({
          englishOnly,
          projectId,
          variableName,
        });
        if (apiResult) {
          if (apiResult.token) {
            dispatch(
              relevancyCheckerInitiateTokenReceived({
                token: apiResult.token,
                projectId,
                variableName,
                status: RelevancyCheckerStatuses.InProgress,
              }),
            );

            // Creating task for each polling request to start at once to keep track of all variables successfully completed relevancy checker process.
            const pollingTask = new Promise((resolve, reject) => dispatch(getRelevancyCheckerInitiateStatus({ token: apiResult.token ?? "", variableName: variableName, resolve, reject })));
            taskList.push(pollingTask);
          }
          isSuccess = true;
        }
      } catch (err) {
          dispatch(
            relevancyCheckerInitiateTokenRemoved({
              token: "",
              projectId,
              variableName,
            }),
          );
        }
      result.push({ variable, isSuccess });
    }
    Promise.all(taskList).then((results) => {
      if(taskList.length !== variables.length)
      {
        throw new Error();
      }
      dispatch(updateResultMessageVariable({projectId:projectId, name:batteryOrVariableFullname}));
      return results;
    }).catch(()=>{
      dispatch(updateErrorMessageVariable({projectId:projectId, name:batteryOrVariableFullname}));
    });

    return result;
  },
);

export const getRelevancyCheckerInitiateStatus = createAsyncThunk(
  "textVariables/getRelevancyCheckerInitiateStatus",
  async ({ token, variableName, resolve, reject }: { token: string; variableName: string, resolve:(value:string)=>void, reject:()=>void }, { dispatch, getState }) => {
    const {
      relevancyChecker: { relevancyCheckerInitiateTokens },
    } = getState() as IState;
    const relevancyCheckerToken = relevancyCheckerInitiateTokens?.find((x) =>
      x.variables?.find((v) => v.token == token),
    );
    const projectId = relevancyCheckerToken && relevancyCheckerToken.projectId;
    try {
      if (!projectId) return;

      const result = await textVariablesClient.getRelevancyCheckerInitiateStatus(projectId ?? 0, token ?? "");
      switch (result?.status) {
        case RelevancyCheckerStatuses.Completed:
          dispatch(
            relevancyCheckerInitiateTokenRemoved({
              token: token,
              projectId,
              variableName,
            }),
          );
          resolve("completed");
          break;
        case RelevancyCheckerStatuses.InProgress:
          setTimeout(() => {
            dispatch(
              relevancyCheckerInitiateTokenReceived({
                token: token,
                projectId,
                variableName,
                status: RelevancyCheckerStatuses.InProgress,
              }),
            );
            dispatch(getRelevancyCheckerInitiateStatus({ token, variableName, resolve, reject }));
          }, POLLING_INTERVAL_MS);
          break;
        default:
          dispatch(
            relevancyCheckerInitiateTokenRemoved({
              token: token,
              projectId,
              variableName,
            }),
          );
          reject();
      }
    } catch (error) {
      dispatch(
        relevancyCheckerInitiateTokenRemoved({
          token: token,
          projectId,
          variableName,
        }),
      );
      reject();
    }
  },
);

export const downloadRelevancyChecker = createAsyncThunk(
  "textVariables/downloadRelevancyChecker",
  async (
    request: DownloadRelevancyCheckerQuery,
    { dispatch, rejectWithValue, getState },
  ) => {
    const {
      project: { currentProject },
    } = getState() as IState;
    try {
      const result = await textVariablesClient.initiateDownloadRelevancyChecker({...request, version:"latest"});
      if (result) {
        if (result.token) {
          setRelevancyDownloadTokenAndGetTokenStatus(dispatch, result.token, request.projectId);
        }
      }
    } catch (error) {
      deleteRelevancyDownloadTokenOnError(dispatch, "", request.projectId, currentProject?.id);
      return rejectWithValue(error);
    }
  },
);

export const getRelevancyCheckerDownloadStatus = createAsyncThunk(
  "textVariables/getRelevancyCheckerDownloadStatus",
  async ({ token }: { token: string }, { dispatch, getState }) => {
    const {
      relevancyChecker: { relevancyCheckerDownloadTokens },
    } = getState() as IState;
    const {
      project: { currentProject },
    } = getState() as IState;
    const projectId: number | undefined = relevancyCheckerDownloadTokens?.find((x) => x.token == token)?.projectId;
    try {
      if (!projectId) return;
      const result = await textVariablesClient.getDownloadStatus(token);
      switch (result?.status) {
        case PollingStatuses.Complete:
          const downloadResults = await textVariablesClient.downloadRelevancyCheckerReport(projectId, "latest", token);
          dispatch(
            downloadRelevancyCheckerTokenRemoved({
              token: token,
              status: LoadingState.succeed,
              projectId: projectId,
              currentProjectId: currentProject?.id,
            }),
          );
          if (downloadResults.presignedUrl) {
            dispatch(setPresignedUrl(downloadResults.presignedUrl));
            saveAs(downloadResults.presignedUrl);
          }
          break;
        case PollingStatuses.InProgress:
          setRelevancyDownloadTokenAndGetTokenStatus(dispatch, token, projectId);
          break;
        default:
          dispatch(
            downloadRelevancyCheckerTokenRemoved({
              token,
              status: LoadingState.failed,
              projectId: projectId,
              errorMessage: result.message,
              currentProjectId: currentProject?.id,
            }),
          );
          break;
      }
    } catch (error) {
      deleteRelevancyDownloadTokenOnError(dispatch, token, projectId, currentProject?.id);
    }
  },
);

export const concatTextVariablesForRelevancy = (variables: TextVariableDto[] = []) => {
  return variables.map((x) => x.variableFullName ?? "").join(", ") ?? "";
};

export const getSlicedVariableNamesForRelevancy = (variables: TextVariableDto[] = []) => {
  return variables
    .map((x) => x.variableFullName ?? "")
    .slice(0, 2)
    .join(", ");
};

const relevancyCheckerSlice = createSlice({
  name: "relevancyChecker",
  initialState: initialRelevancyChecker,
  reducers: {
    resetRelevancyCheckerStatus: (state) => {
      state.initiateRelevancyCheckerStatus = LoadingState.idle;
      state.errorMessage = undefined;
      state.relevancyDownloadingState = LoadingState.idle;
      state.downloadRelevancyCheckerErrorMessage = undefined;
    },
    clearResultForRelevancyVariable:(state, { payload }:{ payload: { name?:string, projectId?:number}}) => {
      const { projectId, name} = payload;
      state.resultMessageVariable = [
        ...state.resultMessageVariable.filter(result=>!(result.projectId===projectId && result.name===name))
      ];
    },
    clearErrorForRelevancyVariable:(state, { payload }:{ payload: { name?:string, projectId?:number}}) => {
      const { projectId, name} = payload;
      state.errorMessageVariable = [
        ...state.errorMessageVariable.filter((result=>result.projectId===projectId && result.name===name))
      ];
    },
    updateResultMessageVariable:(state, {payload}:{payload:{name?:string, projectId:number}}) => {
      state.resultMessageVariable.push({projectId:payload.projectId, name:payload.name});
    },
    updateErrorMessageVariable:(state, {payload}:{payload:{name?:string, projectId:number}}) => {
      state.errorMessageVariable.push({projectId:payload.projectId, name:payload.name});
    },
    relevancyCheckerInitiateTokenReceived: (
      state,
      {
        payload: { token, projectId, variableName, status },
      }: { payload: { token: string; projectId?: number; variableName: string; status: RelevancyCheckerStatuses } },
    ) => {
      if (state.relevancyCheckerInitiateTokens == null) {
        state.relevancyCheckerInitiateTokens = [];
      }
      state.relevancyCheckerInitiateTokens = updateRelevancyCheckerToken(
        state.relevancyCheckerInitiateTokens,
        token,
        projectId,
        variableName,
        status,
      );
    },
    relevancyCheckerInitiateTokenRemoved: (
      state,
      {
        payload: { token, projectId, variableName },
      }: { payload: { token: string; projectId?: number; variableName: string } },
    ) => {
      if (state.relevancyCheckerInitiateTokens && state.relevancyCheckerInitiateTokens.length > 0) {
        state.relevancyCheckerInitiateTokens = deleteRelevancyCheckerToken(
          state.relevancyCheckerInitiateTokens,
          token,
          variableName,
          projectId,
        );
      }
    },
    downloadRelevancyCheckerTokenReceived: (
      state,
      { payload: { token, projectId } }: { payload: { token: string; projectId: number } },
    ) => {
      state.relevancyCheckerDownloadTokens = setRelevancyCheckerDownloadToken(
        state.relevancyCheckerDownloadTokens,
        token,
        projectId,
      );
    },
    downloadRelevancyCheckerTokenRemoved: (
      state,
      {
        payload: { token, status, projectId, currentProjectId, errorMessage },
      }: {
        payload: {
          token: string;
          status: LoadingState;
          projectId?: number;
          currentProjectId?: number;
          errorMessage?: string;
        };
      },
    ) => {
      state.relevancyCheckerDownloadTokens = deleteRelevancyCheckerDownloadToken(
        state.relevancyCheckerDownloadTokens,
        token,
        projectId,
      );
      if (currentProjectId && projectId === currentProjectId) {
        state.relevancyDownloadingState = status;
        if (status == LoadingState.failed) {
          state.downloadRelevancyCheckerErrorMessage = errorMessage
            ? `${RELEVANCY_CHECKER} ${errorMessage}`
            : RELEVANCY_CHECKER_DOWNLOAD_ERROR_MSG;
        } else {
          state.downloadRelevancyCheckerErrorMessage = undefined;
        }
      } else {
        state.downloadRelevancyCheckerErrorMessage = undefined;
      }
    },
    setPresignedUrl(state, action: PayloadAction<string | null>) {
      state.presignedUrl = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      initiateRelevancyCheckerVariable.pending,
      (
        state,
        {
          meta: {
            arg: { variables, projectId },
          },
        },
      ) => {
        state.initiateRelevancyCheckerStatus = LoadingState.pending;
        state.errorMessage = undefined;
        if (state.relevancyCheckerInitiateTokens == null) {
          state.relevancyCheckerInitiateTokens = [];
        }
        for (const variable of variables) {
          state.relevancyCheckerInitiateTokens = updateRelevancyCheckerToken(
            state.relevancyCheckerInitiateTokens,
            "",
            projectId,
            variable.variableName,
            RelevancyCheckerStatuses.InProgress,
          );
        }
      },
    );

    builder.addCase(initiateRelevancyCheckerVariable.fulfilled, (state, { payload }) => {
      state.initiateRelevancyCheckerStatus = payload.every((x) => x.isSuccess)
        ? LoadingState.succeed
        : LoadingState.failed;
      state.RelevancyCheckerVariables = payload.map((x) => x.variable);
      state.errorMessage = undefined;
    });

    builder.addCase(
      initiateRelevancyCheckerVariable.rejected,
      (
        state,
        {
          meta: {
            arg: { variables, projectId },
          },
          error,
        },
      ) => {
        state.initiateRelevancyCheckerStatus = LoadingState.failed;
        state.RelevancyCheckerVariables = variables;
        state.errorMessage = error.message;
        for (const variable of variables) {
          if (state.relevancyCheckerInitiateTokens && state.relevancyCheckerInitiateTokens.length > 0) {
            state.relevancyCheckerInitiateTokens = deleteRelevancyCheckerToken(
              state.relevancyCheckerInitiateTokens,
              "",
              variable.variableName,
              projectId,
            );
          }
        }
      },
    );
    builder.addCase(
      downloadRelevancyChecker.pending,
      (
        state,
        {
          meta: {
            arg: { projectId },
          },
        },
      ) => {
        state.relevancyDownloadingState = LoadingState.pending;
        state.downloadRelevancyCheckerErrorMessage = undefined;
        state.relevancyCheckerDownloadTokens = setRelevancyCheckerDownloadToken(
          state.relevancyCheckerDownloadTokens,
          "",
          projectId,
        );
      },
    );
  },
});
export const {
  resetRelevancyCheckerStatus,
  relevancyCheckerInitiateTokenReceived,
  relevancyCheckerInitiateTokenRemoved,
  downloadRelevancyCheckerTokenReceived,
  downloadRelevancyCheckerTokenRemoved,
  clearResultForRelevancyVariable,
  clearErrorForRelevancyVariable,
  updateResultMessageVariable,
  updateErrorMessageVariable,
  setPresignedUrl
} = relevancyCheckerSlice.actions;
export default relevancyCheckerSlice.reducer;
